import React from 'react';
import { formatDate, formatDateTime } from '../../../Utils/dateFormat';
import { getAssignedBillerDisplay, getClaimedBillerDisplay } from '../../../Utils/assignedBiller';
import CustomAvatar from '../../CustomComponents/CustomAvatar';
import { AssignButton } from '../JobList/styledComponents';
import { Cell, StatusBadge } from './AuditDetailStyles';

const dt = v => (v ? formatDateTime(v) : '—');
const d = v => (v ? formatDate(v) : '—');
const ms = v => (v ? new Date(v).getTime() : null);

/* ============================================================
   Jobs (tab 1)
   ============================================================ */
export const JOB_COLUMNS = [
  {
    key: 'jobNumber',
    label: 'Job Number',
    size: 120,
    text: j => j.jobNumber || j.jobId || '—',
    cell: j => <Cell strong>{j.jobNumber || j.jobId || '—'}</Cell>
  },
  { key: 'author', label: 'Author', size: 150, text: j => j.author || '—' },
  {
    key: 'primaryCarePhysician',
    label: 'Primary Care Physician',
    size: 190,
    text: j => j.primaryCarePhysician || '—'
  },
  {
    key: 'patientName',
    label: 'Patient',
    size: 160,
    text: j => j.patientName || '—'
  },
  {
    key: 'submissionTime',
    label: 'Submission Time',
    size: 160,
    text: j => dt(j.submissionTime),
    sortVal: j => ms(j.submissionTime)
  },
  {
    key: 'effectiveDate',
    label: 'Encounter Date',
    size: 160,
    text: j => dt(j.effectiveDate),
    sortVal: j => ms(j.effectiveDate)
  },
  {
    key: 'facilityName',
    label: 'Facility',
    size: 160,
    text: j => j.facility?.facilityName || '—'
  },
  {
    key: 'status',
    label: 'Status',
    size: 120,
    text: j => j.status || '—',
    cell: j => (
      <div>
        <StatusBadge status={j.status}>{j.status || '—'}</StatusBadge>
      </div>
    )
  },
  { key: 'workType', label: 'Work Type', size: 150, text: j => j.workType || '—' },
  {
    key: 'dictationTemplateName',
    label: 'Template',
    size: 150,
    text: j => j.dictationTemplate?.templateName || '—'
  },
  {
    key: 'assignedBiller',
    label: 'Assigned Biller',
    size: 180,
    text: j => getAssignedBillerDisplay(j) || '—'
  },
  {
    key: 'billerName',
    label: 'Claimed Biller',
    size: 180,
    text: j => getClaimedBillerDisplay(j) || '—'
  },
  {
    key: 'billStatus',
    label: 'Bill Status',
    size: 120,
    text: j => (j.billStatus ? 'Billed' : 'Not Billed')
  },
  {
    key: 'reviewerName',
    label: 'Current Reviewer',
    size: 160,
    text: j => j.reviewerInProgress?.fullName || '—'
  },
  {
    key: 'scribeName',
    label: 'Scribe',
    size: 150,
    text: j => j.scribe?.fullName || '—'
  },
  {
    key: 'currentLevelOfReview',
    label: 'Current Review Level',
    size: 160,
    text: j => j.currentLevelOfReview || '—'
  },
  {
    key: 'submissionToReviewerLevel1Time',
    label: 'Sent to Reviewer',
    size: 170,
    text: j => dt(j.submissionToReviewerLevel1Time),
    sortVal: j => ms(j.submissionToReviewerLevel1Time)
  },
  {
    key: 'rejectedByReviewerTime',
    label: 'Reviewer Rejection Time',
    size: 200,
    text: j => dt(j.rejectedByReviewerTime),
    sortVal: j => ms(j.rejectedByReviewerTime)
  },
  {
    key: 'billedTime',
    label: 'Billed Time',
    size: 160,
    text: j => dt(j.billedTime),
    sortVal: j => ms(j.billedTime)
  },
  {
    key: 'emrSubmissionTime',
    label: 'EMR Submission Time',
    size: 190,
    text: j => dt(j.emrSubmissionTime),
    sortVal: j => ms(j.emrSubmissionTime)
  },
  {
    key: 'cptCodesInContent',
    label: 'CPT Code',
    size: 150,
    text: j => {
      const codes = j.cptCodesInContent;
      if (!codes || !codes.length) return '—';
      return codes.map(c => c.cptCode).filter(Boolean).join(', ') || '—';
    }
  },
  {
    key: 'admissionDate',
    label: 'Admission Date',
    size: 150,
    text: j => d(j.patientDemographics?.admissionDate),
    sortVal: j => ms(j.patientDemographics?.admissionDate)
  },
  {
    key: 'mrn',
    label: 'MRN',
    size: 150,
    text: j => j.patientDemographics?.medicalRecordNumber || '—'
  },
  {
    key: 'dischargeDate',
    label: 'Discharge Date',
    size: 150,
    text: j => d(j.patientDemographics?.dischargeDate),
    sortVal: j => ms(j.patientDemographics?.dischargeDate)
  },
  {
    key: 'mipsScore',
    label: 'MIPS Score',
    size: 110,
    text: j => (j.mips?.score != null ? `${j.mips.score}/100` : '—'),
    sortVal: j => (j.mips?.score != null ? j.mips.score : -1)
  },
  {
    key: 'strike',
    label: 'Strike',
    size: 130,
    action: true, // no sort / filter / drag; pinned last
    cell: (job, ctx) => {
      const canStrike =
        (job.status === 'Completed' || job.status === 'Struck Out') &&
        (job.progressNote !== null || job.practitionerNote !== null) &&
        job.authorId === ctx?.userId;
      if (!canStrike) return <Cell />;
      return (
        <AssignButton
          type='primary'
          disabled={job.status === 'Struck Out'}
          onClick={e => {
            e.stopPropagation();
            ctx.onStrike(job.jobId);
          }}
        >
          Strike Out
        </AssignButton>
      );
    }
  }
];

/* ============================================================
   Users (tab 2)
   ============================================================ */
export const USER_COLUMNS = [
  {
    key: 'fullName',
    label: 'Name',
    size: 190,
    text: u => u.fullName || '—',
    cell: u => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <CustomAvatar url={u.profilePic} size={24} />
        <Cell strong>{u.fullName || '—'}</Cell>
      </div>
    )
  },
  { key: 'email', label: 'Email', size: 220, text: u => u.email || '—' },
  {
    key: 'userType',
    label: 'User Type',
    size: 150,
    text: u => u.userType || '—'
  },
  {
    key: 'loginTime',
    label: 'Login',
    size: 170,
    text: u => dt(u.loginTime),
    sortVal: u => ms(u.loginTime)
  },
  {
    key: 'logoutTime',
    label: 'Logout',
    size: 170,
    text: u => {
      if (u.logoutTime === 0)
        return u.loggingStatus === 'FAILED' ? '-' : 'Active Session';
      return u.logoutTime ? dt(u.logoutTime) : '—';
    },
    sortVal: u => (u.logoutTime ? ms(u.logoutTime) : null)
  },
  {
    key: 'loggedUserIp',
    label: 'IP Address',
    size: 150,
    text: u => u.loggedUserIp || '—'
  },
  {
    key: 'loggingStatus',
    label: 'Status',
    size: 120,
    text: u => u.loggingStatus || '—',
    cell: u => (
      <div>
        <StatusBadge status={u.loggingStatus === 'FAILED' ? 'Failed' : 'Completed'}>
          {(u.loggingStatus || '—').toLowerCase()}
        </StatusBadge>
      </div>
    )
  }
];

/* ============================================================
   Facilities (tab 3)
   ============================================================ */
export const FACILITY_COLUMNS = [
  {
    key: 'facilityName',
    label: 'Facility Name',
    size: 220,
    text: f => f.facilityName || '—',
    cell: f => <Cell strong>{f.facilityName || '—'}</Cell>
  },
  {
    key: 'facilityDisableStatusLastUpdated',
    label: 'Last Updated',
    size: 180,
    text: f =>
      f.facilityDisableStatusLastUpdated
        ? dt(f.facilityDisableStatusLastUpdated)
        : '--',
    sortVal: f => ms(f.facilityDisableStatusLastUpdated)
  },
  {
    key: 'disabled',
    label: 'Facility Status',
    size: 150,
    text: f => (f.disabled ? 'Disabled' : 'Enabled'),
    cell: f => (
      <div>
        <StatusBadge status={f.disabled ? 'Failed' : 'Completed'}>
          {f.disabled ? 'Disabled' : 'Enabled'}
        </StatusBadge>
      </div>
    )
  }
];
